import { db } from '../firebase';
import {
  collection, query, where, orderBy, limit, onSnapshot,
  doc, updateDoc, deleteDoc, writeBatch
} from 'firebase/firestore';
import { getUsersByIds } from './userService';

const NOTIFICATIONS_COLLECTION = 'notifications';

// One listener backs BOTH the badge count and the panel contents, which is
// what keeps this feature's read cost bounded: no separate count query, no
// polling, no second index. Thirty is more history than a bell is ever asked
// for -- anything older belongs to a notifications page we don't have.
export const NOTIFICATION_LIMIT = 30;

// Live subscription to the signed-in user's notifications, newest first.
// Returns the unsubscribe function.
//
// The userId constraint is NOT optional and not merely a filter: the read
// rule is a plain `userId == request.auth.uid` comparison, so Firestore
// requires the query to constrain that field and denies it outright
// otherwise. That constraint is exactly what guarantees this can only ever
// return the caller's own rows. See the notifications block in
// firestore.rules.
export function subscribeToNotifications(uid, callback) {
  if (!uid) return () => {};

  return onSnapshot(
    query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(NOTIFICATION_LIMIT)
    ),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      // A denied or failed listener must resolve the caller's loading state
      // rather than spinning forever -- the same reason subscribeToRecipe
      // takes an error handler. An empty bell is the right degraded state.
      console.error('Error subscribing to notifications:', err);
      callback([]);
    }
  );
}

// Rules allow a client to flip `read` to true and nothing else, so these
// three writes are the entire client-side surface of the collection.
export async function markNotificationRead(notificationId) {
  await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId), { read: true });
}

// One batch rather than N writes. Already-read rows are skipped: rules permit
// setting read to true when it is already true, but paying for the write
// would be pointless.
export async function markAllNotificationsRead(notifications) {
  const unread = (notifications || []).filter((n) => !n.read);
  if (unread.length === 0) return;

  // writeBatch caps at 500, and the listener holds at most
  // NOTIFICATION_LIMIT, so a single batch always suffices here.
  const batch = writeBatch(db);
  unread.forEach((n) => batch.update(doc(db, NOTIFICATIONS_COLLECTION, n.id), { read: true }));
  await batch.commit();
}

export async function dismissNotification(notificationId) {
  await deleteDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId));
}

// Joins each notification's actor to their LIVE profile, so names and photos
// are current and a private or deleted account isn't republished through
// someone else's bell. Same approach, and same reasoning, as getComments.
//
// Batched at one read per ten uids, and deduped first -- ten notifications
// from the same person cost one lookup, not ten. Never throws: a failed join
// degrades to unnamed rows ("Someone"), which is far better than an empty
// bell.
export async function attachActors(notifications) {
  const rows = notifications || [];
  if (rows.length === 0) return [];

  const profiles = await getUsersByIds(rows.map((n) => n.actorId)).catch(() => []);
  const byUid = new Map(profiles.map((p) => [p.uid, p]));

  return rows.map((n) => {
    const actor = byUid.get(n.actorId);
    return {
      ...n,
      actorName: actor?.displayName || null,
      actorPhotoURL: actor?.photoURL || null,
      actorPhotoPosition: actor?.photoPosition || 'center'
    };
  });
}
